import axios, { AxiosResponse } from "axios";
import { z } from "zod";
import { getEnv } from "lib/getEnv";
import { OptionalPromise, ValueOf } from "./types";

const client = axios.create({
  baseURL: getEnv("WP_API_BASE_URL"),
  headers: {
    "Content-Type": "application/json",
  },
  auth: {
    username: getEnv("WP_API_USERNAME") as string,
    password: (getEnv("WP_API_PASSWORD") as string).replace("\\", ""),
  },
});

const parseWordPressData = async <T extends z.ZodTypeAny>(
  schema: T,
  response: OptionalPromise<AxiosResponse>,
): Promise<z.output<T> | undefined> => {
  const baseSchema = z
    .object({
      data: z
        .object({ acfOptions: z.object({ hwaOptions: schema }) })
        .optional(),
    })
    .transform((value) => (value as any).data?.acfOptions.hwaOptions);

  return baseSchema.parse((await response).data);
};

const getSkipBagsDictionary = async () => {
  return parseWordPressData(
    z
      .object({
        dictionary: z
          .object({
            bagContentType: z.string(),
            bagContentPhrases: z.object({ phrase: z.string() }).array(),
          })
          .array(),
      })
      .transform(({ dictionary }) => dictionary),
    client.post("/graphql", {
      query: `{
        acfOptions {
          hwaOptions {
            dictionary {
              bagContentType
              bagContentPhrases {
                phrase
              }
            }
          }
        }
      }`,
    }),
  );
};

const getAccountFAQs = () => {
  return parseWordPressData(
    z
      .object({
        hwaDashboardFaqs: z
          .object({
            answer: z.string(),
            question: z.string(),
          })
          .array(),
      })
      .transform(({ hwaDashboardFaqs }) => hwaDashboardFaqs),
    client.post("/graphql", {
      query: `{
        acfOptions {
          hwaOptions {
            hwaDashboardFaqs {
              answer
              question
            }
          }
        }
    }`,
    }),
  );
};

const getSuccessFAQs = (categorySku: string) => {
  return parseWordPressData(
    z
      .object({
        faqs: z
          .object({
            serviceSku: z.string(),
            faqs: z
              .object({ answer: z.string(), question: z.string() })
              .array(),
          })
          .array(),
      })
      .transform(
        ({ faqs }) =>
          faqs.find(({ serviceSku: faqSku }) => faqSku === categorySku)?.faqs ??
          [],
      ),
    client.post("/graphql", {
      query: `{
        acfOptions {
          hwaOptions {
            faqs {
              serviceSku
              faqs {
                answer
                question
              }
            }
          }
        }
      }`,
    }),
  );
};

export const wordpressClient = {
  getSkipBagsDictionary,
  getAccountFAQs,
  getSuccessFAQs,
  instance: client,
};

export type WordPressReturn<Endpoint extends ValueOf<typeof wordpressClient>> =
  NonNullable<Awaited<ReturnType<Endpoint>>>;
